import { useTranslate, useGo, useNavigation, useImport, useNotification, useExport } from "@refinedev/core";
import { CreateButton, ExportButton } from "@refinedev/mui";
import { ListOutlined, BorderAllOutlined } from "@mui/icons-material";
import { replace, useLocation, useSearchParams } from "react-router";
import { ToggleButtonGroup, ToggleButton, Typography } from "@mui/material";
import {
  ProductListCard,
  ProductListTable,
  RefineListView,
} from "../../components";
import { PropsWithChildren } from "react";
import { useState, useMemo } from "react";
import { IProduct } from "@/utils/zod/productCollectionFormValidation";
import { useTable } from "@refinedev/react-table";
import { ColumnDef } from "@tanstack/react-table";
import { ImportButton } from "@/components/override/ImportButton";
import { productImportCSV } from "@/utils/data-provider/csvImportSchema";

type TView = "table" | "card";

export const ProductList = ({ children }: PropsWithChildren) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<TView>(() => {
    const view = localStorage.getItem("product-view") as TView;
    return view || "table";
  });
  const [importProgress, setImportProgress] = useState<{
    processedAmount: number;
    totalAmount: number;
  } | null>(null);
  
  // Get current status from URL or default to "active"
  const currentStatus = searchParams.get("status") as "archived" | "active" | "draft";
  const tableTab = currentStatus && ["archived", "active", "draft"].includes(currentStatus) ? currentStatus : "active";
  
  const setTableTab = (newStatus: "archived" | "active" | "draft") => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("status", newStatus);
    setSearchParams(newSearchParams, { replace: true });
  };
  
  const t = useTranslate();
  const { createUrl } = useNavigation();
  const go = useGo();
  const { pathname } = useLocation();
  const {open} = useNotification();

  // Define columns for TanStack Table
  const columns = useMemo<ColumnDef<IProduct>[]>(
    () => [
      {
        id: "id",
        header: t("products.fields.id"),
        accessorKey: "id",
        meta: { filterOperator: "eq" },
      },
      {
        id: "name",
        header: t("products.fields.name"),
        accessorKey: "name",
        meta: { filterOperator: "contains" },
      },
      {
        id: "sku_id",
        header: t("products.fields.sku_id"),
        accessorKey: "sku_id",
        meta: { filterOperator: "contains" },
      },
      {
        id: "cost_price",
        header: t("products.fields.cost_price"),
        accessorKey: "cost_price",
        meta: { filterOperator: "eq" },
      },
      {
        id: "minimum_order_quantity",
        header: t("products.fields.moq"),
        accessorKey: "minimum_order_quantity",
        meta: { filterOperator: "eq" },
      },
      {
        id: "uom",
        header: t("products.fields.uom"),
        accessorKey: "uom",
        meta: { filterOperator: "eq" },
      },
      {
        id: "available_quantity",
        header: t("products.fields.available_quantity"),
        accessorKey: "available_quantity",
        meta: { filterOperator: "eq" },
      },
      {
        id: "status",
        header: t("products.fields.status.label"),
        accessorKey: "status",
        meta: { filterOperator: "eq" },
      }
    ],
    []
  );

  // Use TanStack Table with Refine - fetch products based on status
  const tableProps = useTable<IProduct>({
    refineCoreProps: {
      resource: `products/status/${tableTab}`,
      sorters: {
        initial: [{ field: "id", order: "desc" }]
      },
    },
    columns,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 12,
      },
    },
    getRowId: (row) => row.id?.toString() ?? '',
  });



  const tableQuery = {
    data: {
      data: tableProps.refineCore.tableQuery.data?.data ?? [],
      total: tableProps.refineCore.tableQuery.data?.total ?? 0
    },
    isLoading: tableProps.refineCore.tableQuery.isLoading,
    isError: tableProps.refineCore.tableQuery.isError,
    selectedRows: tableProps.getState().rowSelection,
    setSelectedRows: tableProps.setRowSelection,
    resetRowSelection: tableProps.resetRowSelection,
  };

  const handleViewChange = (
    _e: React.MouseEvent<HTMLElement>,
    newView: TView,
  ) => {
    // remove query params (pagination, filters, etc.) when changing view
    replace("");

    setView(newView);
    localStorage.setItem("product-view", newView);
  };

  // Extract pagination values from tableProps
  const current = tableProps.getState().pagination.pageIndex + 1;
  const pageSize = tableProps.getState().pagination.pageSize;
  const setCurrent = (page: number) => {
    tableProps.setPageIndex(page - 1);
  };
  const setPageSize = (size: number) => {
    tableProps.setPageSize(size);
  };

  const { inputProps, isLoading: isImporting } = useImport({
    resource: "products/bulk-update",
    onFinish: (data) => {
      data.succeeded?.[0]?.response.forEach((response) => {
        if (response.status === "error") {
          open?.({
            message: "Import Error",
            description: response.error,
            type: "error",
          });
        } else {
          open?.({
            message: "Import Success",
            description: response.message,
            type: "success",
          });
        }
      });
      // Refetch the product list after import completes
      tableProps.refineCore.tableQuery.refetch();
    },
    onProgress: (data) => {
      setImportProgress(data);
    },
    mapData: (data) => {
      console.log(data)
      return data.sku_id ? {
        ...data,
        status: data.available_quantity > 0 ? data.status : "archived",
        features: data.features.split(","),
      } : {
        ...data,
        status: data.available_quantity > 0 ? data.status : "archived",
        sku_id: ""
      }
    }
  })

  const { triggerExport, isLoading: isExporting } = useExport({
    resource: "products",
    download: true,
    mapData: (data) => {
      const mappedData = {
        name: data.name,
        sku_id: data.sku_id,
        description: data.description,
        cost_price: data.cost_price,
        minimum_order_quantity: data.minimum_order_quantity,
        uom: data.uom,
        available_quantity: data.available_quantity,
        features: data.features,
        material: data.material,
        rating_stars: data.rating_stars,
        review_count: data.review_count,
        status: data.status,
      }
      return mappedData
    }
  })

  return (
    <>
      <RefineListView
        headerButtons={(props) => [
          <ToggleButtonGroup
            key="table-tab-toggle"
            value={tableTab}
            exclusive
            onChange={ (_e, value) => setTableTab(value) }
          >
            <ToggleButton value="active" size="small">
              <Typography>{t("products.fields.status.active")}</Typography>
            </ToggleButton>
            <ToggleButton value="draft" size="small">
              <Typography>{t("products.fields.status.draft")}</Typography>
            </ToggleButton>
            <ToggleButton value="archived" size="small">
              <Typography>{t("products.fields.status.archived")}</Typography>
            </ToggleButton>
          </ToggleButtonGroup>,
          <Typography variant="body2" color="text.secondary">
          {isImporting ? `Importing... ${importProgress?.processedAmount}/${importProgress?.totalAmount}` : isExporting ? "Exporting..." : undefined}
        </Typography>,
        <ExportButton onClick={triggerExport} disabled={isImporting || isExporting} />,
        <ImportButton
          inputProps={inputProps}
          csvHeaders={productImportCSV}
          disabled={isImporting || isExporting}
          type="product"
        />,
          <ToggleButtonGroup
            key="view-toggle"
            value={view}
            exclusive
            onChange={handleViewChange}
          >
            <ToggleButton value="table" aria-label="table view" size="small">
              <ListOutlined />
            </ToggleButton>
            <ToggleButton value="card" aria-label="card view" size="small">
              <BorderAllOutlined />
            </ToggleButton>
          </ToggleButtonGroup>,
          <CreateButton
            {...props.createButtonProps}
            key="create"
            size="medium"
            sx={{ height: "40px" }}
            onClick={() => {
              return go({
                to: `${createUrl("products")}`,
                query: {
                  to: pathname,
                },
                options: {
                  keepQuery: true,
                },
                type: "replace",
              });
            }}
          >
            {t("products.actions.add")}
          </CreateButton>,
        ]}
      >
        {view === "table" && <ProductListTable tableQuery={tableQuery} />}
        
        {view === "card" && (
          <ProductListCard 
            tableQuery={tableQuery}
            filters={tableProps.refineCore.filters}
            setFilters={tableProps.refineCore.setFilters}
            current={current}
            setCurrent={setCurrent}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        )}
      </RefineListView>
      {children}
    </>
  );
};
